import { Grid, Box, Typography } from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import WSDataGridNew, {
  IColumns,
} from "../../../Components/DataGrid/WSDataGridNew";
import WSButton from "../../../Components/Button/WSButton";
import { WSIcons } from "../../../commonFunctions/iIcons";
import WSFilterButtonGroup from "../../../Components/Filter/WSFilterButtonGroup";
import WSDashboardCard from "../../../Components/CardListing/WSDashboardCard";
import { useNavigate } from "react-router-dom";
import {
  updateMainHeader,
  getLocalValue,
  LoacalVariables,
} from "../../../commonFunctions/CommonFunctions";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import SwipeableDrawerForm from "../../../Components/DrawyerForm/DrawyerForm";
import { useForm } from "react-hook-form";
import http from "../../../commonFunctions/httpClient";
import LabTechnicianDrawer from "./LabTechnicianDrawer";

interface TechnicianSummary {
  totalDeliveryPersons: number;
  activeDeliveryPersons: number;
  onTaskDeliveryPersons: number;
}

interface Technician {
  id: string;
  profileName: string;
  profileImage?: string;
  mode: string;
  price: string;
  status: string;
  actions: string;
  contact?: string;
  qualification?: string;
  experience?: string;
}

export const TechnicianManagement: React.FC<any> = React.memo((props) => {
  const { control, register, setValue, reset } = useForm<any>();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formComponent, setFormComponent] = useState<any>();
  const [labId, setLabId] = useState<string>("");
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [filteredTechnicians, setFilteredTechnicians] = useState<Technician[]>(
    []
  );
  const [summary, setSummary] = useState<TechnicianSummary>({
    totalDeliveryPersons: 0,
    activeDeliveryPersons: 0,
    onTaskDeliveryPersons: 0,
  });
  const [loading, setLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false);
  const [filterCounts, setFilterCounts] = useState({
    All: 0,
    Active: 0,
    "On Route": 0,
    Assigned: 0,
    Offline: 0,
  });
  const filters = ["All", "Active", "On Route", "Assigned", "Offline"];
  const [selectedFilter, setSelectedFilter] = useState(filters[0]); // ✅ Load vendorId on mount

  useEffect(() => {
    const userId = getLocalValue(LoacalVariables.UserId);
    if (userId) {
      console.log("🚀 Loading vendorId:", userId);
      setLabId(userId);
    }
  }, []);

  const fetchTechnicians = useCallback(async () => {
    if (!labId) {
      setLoading(false);
      setDataReady(false);
      return;
    }

    try {
      setLoading(true);
      console.log("📡 FETCHING TECHNICIANS:", labId);
      const response: any = await http.get(
        `lab/Technician/${labId}/Technician`
      );
      console.log("✅ FULL API RESPONSE:", response);
      let data: Technician[] = [];
      if (response?.success && Array.isArray(response.data)) {
        data = response.data.map((tech: any) => {
          let formattedAssignment = "No Assignment";
          if (tech.currentAssignment) {
            try {
              const assignment =
                typeof tech.currentAssignment === "string"
                  ? JSON.parse(tech.currentAssignment)
                  : tech.currentAssignment;
              formattedAssignment = [
                assignment.orderId || "",
                assignment.customerName || "",
                assignment.address || "",
                assignment.assignedAt
                  ? new Date(assignment.assignedAt).toLocaleString()
                  : "",
              ]
                .filter(Boolean)
                .join("\n");
            } catch (e) {
              formattedAssignment = tech.currentAssignment;
            }
          }

          return {
            id: tech.technicianId,
            profileName: tech.name,
            profileImage: tech.profileImage
              ? `https://melpapi.reobeen.com${tech.profileImage}`
              : undefined,
            mode: formattedAssignment,
            price: `${tech.todaysPerformance?.ordersCompleted || 0} orders`,
            status: tech.status || "Offline",
            actions: tech.technicianId,
            contact: tech.contact,
            qualification: tech.qualification,
            experience: `${tech.experience || 0} yrs`,
          };
        });
      }
      console.log("✅ Loaded technicians:", data.length);
      setTechnicians(data);
      setDataReady(true);
      const counts = {
        All: data.length,
        Active: data.filter((t) => t.status === "Active").length,
        "On Route": data.filter((t) => t.status === "On Route").length,
        Assigned: data.filter((t) => t.status === "Assigned").length,
        Offline: data.filter((t) => t.status === "Offline").length,
      };
      setFilterCounts(counts);
      setFilteredTechnicians(data);
    } catch (error: any) {
      console.error("💥 API ERROR:", error);
      setTechnicians([]);
      setFilterCounts({
        All: 0,
        Active: 0,
        "On Route": 0,
        Assigned: 0,
        Offline: 0,
      });
      setFilteredTechnicians([]);
      setDataReady(true);
    } finally {
      setLoading(false);
    }
  }, [labId]);

  useEffect(() => {
    if (labId) {
      fetchTechnicians();
    }
  }, [labId, fetchTechnicians]);

  const fetchTechnicianSummary = useCallback(async () => {
    if (!labId) return;
    try {
      const response: any = await http.get(
        `lab/Technician/${labId}/Labtechnician-summary`
      );
      if (response?.success && response?.data) {
        setSummary({
          totalDeliveryPersons: response.data.totalDeliveryPersons || 0,
          activeDeliveryPersons: response.data.activeDeliveryPersons || 0,
          onTaskDeliveryPersons: response.data.onTaskDeliveryPersons || 0,
        });
      }
    } catch (error: any) {
      console.error("💥 Summary Error:", error);
    }
  }, [labId]);

  const handleFilterChange = useCallback(
    (value: string) => {
      const originalFilter = value.replace(/\(\d+\)$/, "").trim();
      console.log("🔄 Filter changed to:", originalFilter);
      setSelectedFilter(originalFilter);

      if (originalFilter === "All") {
        setFilteredTechnicians(technicians);
      } else {
        const filtered = technicians.filter(
          (tech) => tech.status === originalFilter
        );
        setFilteredTechnicians(filtered);
      }
    },
    [technicians]
  );

  const getFilterLabel = useCallback(
    (filter: string) =>
      `${filter}(${filterCounts[filter as keyof typeof filterCounts] || 0})`,
    [filterCounts]
  );

  useEffect(() => {
    updateMainHeader("Technician Management");
  }, []);

  useEffect(() => {
    breadCrumbsStore.updateBreadCrumbs([
      { "Technician Management": "lab-technician-manage/list" },
    ]);
  }, []);

  const handleDelete = useCallback(
    async (id?: string | number) => {
      if (!id || !window.confirm("Delete technician?")) return;
      const idStr = id.toString();
      try {
        console.log("🗑️ DELETING:", idStr);
        const response: any = await http.del(`lab/Technician/${idStr}`);
        if (response?.success !== false) {
          alert("✅ Technician deleted successfully!");
          await Promise.all([fetchTechnicians(), fetchTechnicianSummary()]);
        } else {
          alert("❌ Delete failed");
        }
      } catch (error: any) {
        console.error("💥 DELETE ERROR:", error);
        alert("❌ Failed to delete");
      }
    },
    [labId, fetchTechnicians, fetchTechnicianSummary]
  );

  const handleAction = useCallback(
    (action: string, row: Technician) => {
      console.log("🔧 ACTION:", action, "ROW:", row);
      switch (action) {
        case "View":
          navigate(`/lab-technician-details/${row.id}`);
          break;
        case "Order":
          navigate(`/lab-assign-order/${row.id}`);
          break;
        case "Delete":
          handleDelete(row.id);
          break;
        default:
          console.warn("Unknown action:", action);
      }
    },
    [navigate, handleDelete]
  );

  const handleClick = () => {
    const form: any = (
      <LabTechnicianDrawer
        id="technicianAdd"
        control={control}
        register={register}
        reset={reset}
        setValue={setValue}
        onCancel={() => {
          reset();
          setDrawerOpen(false);
          setFormComponent(null);
        }}
        onSuccess={fetchTechnicians}
      />
    );
    setFormComponent(form);
    setTimeout(() => setDrawerOpen(true), 10);
  };
  if (loading) {
    return (
      <Grid container spacing={2} mt={1} sx={{ paddingLeft: 1 }}>
               {" "}
        <Grid
          item
          sm={12}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
                    <div>Loading technicians...</div>       {" "}
        </Grid>
             {" "}
      </Grid>
    );
  }

  const columns: IColumns[] = [
    { header: "Technician Profile", fieldName: "profileName", type: "default" },
    { header: "Current Assignment", fieldName: "mode", type: "default" },
    { header: "Todays Performance", fieldName: "price", type: "default" },
    { header: "Status", fieldName: "status", type: "status" },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: ["View", "Order", "Delete"],
      actionCallback: (action, row) => handleAction(action, row as Technician),
    },
  ];

  return (
    <>
      <Grid container spacing={2}>
        <Grid item sm={3}>
          <WSDashboardCard
            title="Total Technicians"
            value={filterCounts.All.toString()}
            percentage="+2.5%"
            bottomIcon={<WSIcons.TrendingUp />}
            icon={<WSIcons.ShoppingCartIcon sx={{ color: "#098963" }} />}
          />
        </Grid>
        <Grid item sm={3} xs={12}>
          <WSDashboardCard
            title="Active Now"
            value={filterCounts.Assigned.toString()}
            percentage="+1.2%"
            bottomIcon={<WSIcons.TrendingUp />}
            icon={<WSIcons.InStockIcon sx={{ color: "#098963" }} />}
          />
        </Grid>
        <Grid item sm={3} xs={12}>
          <WSDashboardCard
            title="Offline"
            value={filterCounts.Offline.toString()}
            percentage="-0.8%"
            bottomIcon={<WSIcons.TrendingUp />}
            icon={<WSIcons.OutOfStockIcon sx={{ color: "#ff9800" }} />}
          />
        </Grid>
        <Grid item sm={3} xs={12}>
          <WSDashboardCard
            title="List Loaded"
            value={technicians.length.toString()}
            percentage="100%"
            icon={<WSIcons.ShoppingCartIcon sx={{ color: "#757575" }} />}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={2}>
        <Grid item sm={9}>
          <WSFilterButtonGroup
            options={filters.map((filter) => getFilterLabel(filter))}
            selected={getFilterLabel(selectedFilter)}
            onChange={handleFilterChange}
          />
        </Grid>
        <Grid item sm={3} display="flex" justifyContent="flex-end">
          <WSButton
            id="btnAddTechnician"
            label="Add Technician"
            startIcon={<WSIcons.AddIcon />}
            onClick={handleClick}
            sx={{
              borderRadius: "999px",
              backgroundColor: "#098963!important",
              textTransform: "none",
              fontWeight: 500,
              height: "35px!important",
              fontSize: "0.875rem!important",
              paddingX: 3,
              paddingY: 1,
              "&:hover": { backgroundColor: "#087654!important" },
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={2} sx={{ paddingLeft: 1 }}>
        <Grid item sm={12}>
          <WSDataGridNew
            columns={columns}
            id="technicianListGrid"
            title={`Technicians - ${selectedFilter} (${filteredTechnicians.length})`}
            exportCsv={true}
            data={filteredTechnicians}
            addButton={{ label: "New Technician", color: "secondary" }}
            onAddButtonClick={handleClick}
            searchBox={{ fieldName: "profileName" }}
            gridHeaderSx={{
              backgroundColor: "whitesmoke",
              border: "1px solid whitesmoke",
            }}
          />
        </Grid>
      </Grid>
            {/* ✅ Drawer with SAVE BUTTON NOW VISIBLE */}
      <SwipeableDrawerForm
        anchor="right"
        title="Add New Technician"
        openForm={drawerOpen}
        isEdit={false} // ✅ Shows "Save" button
        id="technicianAdd"
        formComponent={formComponent}
        onClose={() => {
          reset();
          setDrawerOpen(false);
          setFormComponent(null);
        }}
      />
    </>
  );
});

export default TechnicianManagement;
